import * as maplibregl from 'maplibre-gl';
import type { Place } from '../../api/types';
import { circlePolygon } from '../../utils/geo';
import { iconClass } from '../../utils/icons';
import { palette } from '../../utils/palette';

// placeColor is the theme's green, apart from the blue of accuracy circles.
export function placeColor(): string {
  return palette('green');
}

const EMPTY: GeoJSON.FeatureCollection = { type: 'FeatureCollection', features: [] };

// PlaceLayer draws a group's places on a map: a disc per place in map layers
// and the name as a DOM label, because the fallback style has no glyphs for
// text layers. Attach it before other overlays so members draw above places,
// and again after every style change; the labels are DOM markers and stay.
export class PlaceLayer {
  private labels = new Map<string, maplibregl.Marker>();
  private shown: GeoJSON.FeatureCollection = EMPTY;

  constructor(private map: maplibregl.Map, onClick?: (id: string) => void) {
    // Layer events are kept by layer id, so they survive a style change.
    if (onClick) {
      map.on('click', 'places-fill', (e) => {
        const id = e.features?.[0]?.properties?.id;
        if (typeof id === 'string') onClick(id);
      });
      map.on('mouseenter', 'places-fill', () => (map.getCanvas().style.cursor = 'pointer'));
      map.on('mouseleave', 'places-fill', () => (map.getCanvas().style.cursor = ''));
    }
  }

  // attach adds the place layers to the map's current style and draws the
  // places last set.
  attach() {
    const color = placeColor();
    this.map.addSource('places', { type: 'geojson', data: this.shown });
    this.map.addLayer({ id: 'places-fill', type: 'fill', source: 'places', paint: { 'fill-color': color, 'fill-opacity': 0.12 } });
    this.map.addLayer({ id: 'places-line', type: 'line', source: 'places', paint: { 'line-color': color, 'line-width': 2, 'line-opacity': 0.7 } });
  }

  // set draws places, leaving out hidden, which a place editor draws itself.
  set(places: Place[], hidden: string | null = null) {
    const shown = places.filter((p) => p.id !== hidden);
    const features: GeoJSON.Feature[] = shown.map((p) => ({
      type: 'Feature',
      properties: { id: p.id },
      geometry: circlePolygon(p.lat, p.lon, p.radius_m),
    }));
    this.shown = { type: 'FeatureCollection', features };
    (this.map.getSource('places') as maplibregl.GeoJSONSource | undefined)?.setData(this.shown);

    const seen = new Set<string>();
    for (const p of shown) {
      seen.add(p.id);
      let label = this.labels.get(p.id);
      if (!label) {
        const el = document.createElement('div');
        el.className = 'place-label';
        label = new maplibregl.Marker({ element: el }).setLngLat([p.lon, p.lat]).addTo(this.map);
        this.labels.set(p.id, label);
      }
      label.setLngLat([p.lon, p.lat]);
      const el = label.getElement();
      el.textContent = '';
      const icon = document.createElement('i');
      icon.setAttribute('aria-hidden', 'true');
      icon.className = `${iconClass(p.icon ?? 'pin')} mr-1`;
      el.append(icon, p.name);
    }
    for (const [id, label] of this.labels) {
      if (!seen.has(id)) {
        label.remove();
        this.labels.delete(id);
      }
    }
  }
}
